
import { convertCentsToDollarsDecimal } from '@/utils/helpers';
import { type ApiPaginationMetadata, type ApiSortParams } from './api';
import { type AcademicYearEnum } from './athletes';
import { TeamPosition } from './positions';
import type { AthleteDisplayItemProps } from '@/components/common/AthleteDisplayItem.vue';

export const DAY_OF_WEEK = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;

export type DayOfWeekEnum = typeof DAY_OF_WEEK[keyof typeof DAY_OF_WEEK];

export const AGREEMENT_STATUS_ENUM = {
  DRAFT: 0,
  ACTIVE: 10,
  COMPLETED: 20,
  EXPIRED: 30,
  CANCELED: 40,
} as const;
export type AgreementStatusEnum = typeof AGREEMENT_STATUS_ENUM[keyof typeof AGREEMENT_STATUS_ENUM];

export const AGREEMENT_SORT_FIELDS = ['date', 'name'] as const;
export const AGREEMENT_BY_CONTRACT_SORT_FIELDS = ['full_name', 'name', 'status', 'date'] as const;

export const AgreementStatusMapping: Record<AgreementStatusEnum, string> = {
  [AGREEMENT_STATUS_ENUM.DRAFT]: 'draft',
  [AGREEMENT_STATUS_ENUM.ACTIVE]: 'active',
  [AGREEMENT_STATUS_ENUM.COMPLETED]: 'completed',
  [AGREEMENT_STATUS_ENUM.EXPIRED]: 'expired',
  [AGREEMENT_STATUS_ENUM.CANCELED]: 'canceled',
};

export type AgreementStatus = typeof AgreementStatusMapping[keyof typeof AgreementStatusMapping];

export const AGREEMENT_TYPE_ENUM = {
  INSTITUTIONAL_NIL: 0,
  SCHOLARSHIP: 10,
  THIRD_PARTY_NIL: 20,
  OTHER: 255,
} as const;

export type AgreementTypeEnum = typeof AGREEMENT_TYPE_ENUM[keyof typeof AGREEMENT_TYPE_ENUM];

export const PAYMENT_TYPE_ENUM = {
  WALLET: 0,
  EXTERNAL: 10,
} as const;

export type PaymentTypeEnum = typeof PAYMENT_TYPE_ENUM[keyof typeof PAYMENT_TYPE_ENUM];

export const FREQUENCY_TYPE_ENUM = {
  ONE_TIME: 0,
  WEEKLY: 10,
  TWICE_A_MONTH: 20,
  MONTHLY: 30,
  QUARTERLY: 40,
  CUSTOM: 50,
} as const;

export type FrequencyTypeEnum = typeof FREQUENCY_TYPE_ENUM[keyof typeof FREQUENCY_TYPE_ENUM];

export const frequencyTypeMapping: Record<FrequencyTypeEnum, string> = {
  [FREQUENCY_TYPE_ENUM.ONE_TIME]: 'oneTime',
  [FREQUENCY_TYPE_ENUM.WEEKLY]: 'weekly',
  [FREQUENCY_TYPE_ENUM.TWICE_A_MONTH]: 'twiceAMonth',
  [FREQUENCY_TYPE_ENUM.MONTHLY]: 'monthly',
  [FREQUENCY_TYPE_ENUM.QUARTERLY]: 'quarterly',
  [FREQUENCY_TYPE_ENUM.CUSTOM]: 'custom',
};

export const TWICE_A_MONTH_OPTION = {
  FIRST_AND_FIFTEENTH: 0,
  FIFTEENTH_AND_LAST: 10,
} as const;

export type TwiceAMonthOptionEnum = typeof TWICE_A_MONTH_OPTION[keyof typeof TWICE_A_MONTH_OPTION];

export const AGREEMENT_ATHLETE_STATUS_ENUM = {
  PENDING: 0,
  PENDING_CONTRACT_CREATE: 10,
  REVIEW_IN_PROGRESS: 11,
  REVIEWED: 12,
  CREATED_WITHOUT_CONTRACT: 15,
  FAILED_TO_CREATE: 20,
  CREATED: 30,
  SIGNING_IN_PROGRESS: 35,
  EXPIRED: 40,
  SIGNED: 50,
  CERTIFIED: 60,
  CANCELED: 70,
} as const;

export type AgreementAthleteStatusEnum = typeof AGREEMENT_ATHLETE_STATUS_ENUM[keyof typeof AGREEMENT_ATHLETE_STATUS_ENUM];


export const SIGNATURE_STATUS_ENUM = {
  AWAITING_OTHERS: 0,
  PENDING: 1,
  SIGNED: 2,
} as const;

export type SignatureStatusEnum = typeof SIGNATURE_STATUS_ENUM[keyof typeof SIGNATURE_STATUS_ENUM];

export const SignatureStatusMapping: Record<SignatureStatusEnum, string> = {
  [SIGNATURE_STATUS_ENUM.AWAITING_OTHERS]: 'Unsigned',
  [SIGNATURE_STATUS_ENUM.PENDING]: 'Pending',
  [SIGNATURE_STATUS_ENUM.SIGNED]: 'Signed',
};

export const COLLECTION_ENUM = {
  DEFAULT: 'default',
  SIGNED: 'signed',
  CERTIFIED: 'certified',
} as const;

type CollectionEnum = typeof COLLECTION_ENUM[keyof typeof COLLECTION_ENUM];
export type AthletePaymentRequest = {
  value: number;
  date: string;
};


export const PAYMENT_SCHEDULE_ATTACHMENT_LOCATION_ENUM = {
  PREPEND: 0,
  APPEND: 1,
} as const;

export type PaymentScheduleAttachmentLocationEnum = typeof PAYMENT_SCHEDULE_ATTACHMENT_LOCATION_ENUM[keyof typeof PAYMENT_SCHEDULE_ATTACHMENT_LOCATION_ENUM];

export interface ContractReviewer {
  first_name: string;
  last_name: string;
  email: string;
}

export type AthletePaymentsRequest = {
  athlete: string;
  payments: Array<AthletePaymentRequest>;
  academic_year_terms?: ScholarshipAcademicYearTerm[] | null;
  reviewers?: ContractReviewer[];
};

export const ATHLETE_GROUP_NAME = 'Athlete';

export interface ApiSigningGroup {
  twrn: string;
  name: string;
}

export interface AgreementSigneeDefault {
  twrn: string;
  isAthlete: false;
  email: string;
  name: string;
  groupName: string;
}

export interface AgreementSigneeAthlete {
  twrn: string;
  isAthlete: true;
}

export type AgreementSignee = AgreementSigneeDefault | AgreementSigneeAthlete;

export type ApiAgreementParticipant = {
  twrn: string; // signing_group twrn from template
  signing_order: number;
} & ({
  is_athlete: true;
} | {
  is_athlete: false;
  name: string; // not required for draft
  email: string; // not required for draft
});

export interface CreateOrUpdateAgreementRequestModel {
  team_budget?: string | null;
  team_budgets?: string[] | null;
  name: string;
  academic_year_date_id?: string | null;
  academic_year_date_ids?: string[] | null;
  status: AgreementStatusEnum;
  agreement_type: AgreementTypeEnum;
  payment_type: PaymentTypeEnum;
  expiration_date: string | null;
  description: string | null;
  attach_payment_schedule: PaymentScheduleAttachmentLocationEnum | null;
  contact_entity_name: string;
  contact_point_of_contact: string;
  contact_email: string;
  frequency_type: FrequencyTypeEnum;
  payment_start_date: string;
  payment_end_date: string | null;
  custom_payment_schedule: Array<any> | null;
  weekly_day: DayOfWeekEnum | null;
  twice_a_month_option: TwiceAMonthOptionEnum | null;
  athletes: Array<AthletePaymentsRequest> | null;
  contract_template: string | null;
  participants: ApiAgreementParticipant[];
  reviewers?: Record<string, ContractReviewer[]>;
}

export interface ScholarshipAddendumPreferences {
  includeScholarshipValue: boolean;
  includePackageName: boolean;
  awardCategoryTwrns: string[];
  includeAwardCategoryValues: boolean;
  includeEquivalency: boolean;
  includeEquivalencyPct: boolean;
}

export const createDefaultScholarshipAddendumPreferences = (): ScholarshipAddendumPreferences => ({
  includeScholarshipValue: true,
  includePackageName: true,
  awardCategoryTwrns: [],
  includeAwardCategoryValues: true,
  includeEquivalency: true,
  includeEquivalencyPct: true,
});

// Every display field suppressed. Used when no addendum is attached: the terms are
// still built (so budget_value reaches the BE for the contract total) but carry no
// addendum content — leaving budget_value as the only populated value.
export const createEmptyScholarshipAddendumPreferences = (): ScholarshipAddendumPreferences => ({
  includeScholarshipValue: false,
  includePackageName: false,
  awardCategoryTwrns: [],
  includeAwardCategoryValues: false,
  includeEquivalency: false,
  includeEquivalencyPct: false,
});

export class AgreementForm {
  teamBudget: string | null = null;
  teamBudgets: string[] | null = null;
  budgetGroupTwrn: string | null = null;
  name: string;
  academicYearDateId: string | null = null;
  academicYearDateIds: string[] = [];
  status: number = AGREEMENT_STATUS_ENUM.DRAFT;
  agreementType: number = AGREEMENT_TYPE_ENUM.OTHER;
  paymentType: number = PAYMENT_TYPE_ENUM.WALLET;
  expirationDate: string | null;
  description: string | null = null;
  attachPaymentSchedule: PaymentScheduleAttachmentLocationEnum | null = null;
  contactEntityName: string;
  contactPointOfContact: string;
  contactEmail: string;
  frequencyType: number | null = null;
  paymentStartDate: string = '';
  paymentEndDate: string | null = null;
  customPaymentSchedule: Array<any> | null = null;
  weeklyDay: DayOfWeekEnum | null = null;
  twiceAMonthOption: TwiceAMonthOptionEnum | null = null;
  athletes: Array<AthletePaymentsRequest> | null = null;
  contractTemplate: string | null = null;
  signees: AgreementSignee[] = [];
  withoutPaymentSchedule: boolean = false;
  scholarshipAddendum: ScholarshipAddendumPreferences = createDefaultScholarshipAddendumPreferences();
  // One-shot holder for terms loaded from a persisted scholarship draft. Cleared
  // by the contract step once the addendum toggles are derived; not part of the
  // wire payload.
  persistedAcademicYearTerms: ScholarshipAcademicYearTerm[] | null = null;

  constructor(data: CreateOrUpdateAgreementRequestModel) {
    this.teamBudget = data.team_budget ?? null;
    this.teamBudgets = data.team_budgets ?? null;
    this.name = data.name;
    this.academicYearDateId = data.academic_year_date_id ?? null;
    this.academicYearDateIds = data.academic_year_date_ids ?? [];
    this.status = data.status;
    this.agreementType = data.agreement_type;
    this.paymentType = data.payment_type;
    this.expirationDate = data.expiration_date;
    this.description = data.description;
    this.attachPaymentSchedule = data.attach_payment_schedule;
    this.contactEntityName = data.contact_entity_name;
    this.contactPointOfContact = data.contact_point_of_contact;
    this.contactEmail = data.contact_email;
    this.frequencyType = data.frequency_type;
    this.paymentStartDate = data.payment_start_date;
    this.paymentEndDate = data.payment_end_date;
    this.customPaymentSchedule = data.custom_payment_schedule;
    this.weeklyDay = data.weekly_day;
    this.twiceAMonthOption = data.twice_a_month_option;
    this.athletes = data.athletes;
    this.contractTemplate = data.contract_template;
    this.signees = AgreementForm.makeSignees(data.participants);
  }

  static makeSignees(participants: ApiAgreementParticipant[]): AgreementSignee[] {
    return participants.map((participant) => {
      if (participant.is_athlete) {
        return {
          twrn: participant.twrn,
          isAthlete: true,
        };
      }

      return {
        twrn: participant.twrn,
        isAthlete: false,
        name: participant.name,
        email: participant.email,
        groupName: 'N/A',
      };
    });
  }
}

export type AthleteProfileSchema = {
  twrn: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string | null;
};

export class AthleteProfile {
  twrn: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatar: string | null;

  constructor(data: AthleteProfileSchema) {
    this.twrn = data.twrn;
    this.firstName = data.first_name;
    this.fullName = `${data.first_name} ${data.last_name}`;
    this.lastName = data.last_name;
    this.avatar = data.profile_photo_url;
  }
}

export interface AgreementTeamBudget {
  twrn: string;
  name: string;
}

export interface ApiAgreement {
  twrn: string;
  name: string;
  status: AgreementStatusEnum;
  agreement_type: AgreementTypeEnum;
  athlete_profiles: Array<AthleteProfileSchema>;
  total_athletes: number;
  amount: number;
  original_agreement_amount: number;
  date_created: string;
  budget: AgreementBudget | null;
  budgets?: AgreementBudget[];
  can_update_budget: boolean;
}

export interface ApiAthleteAgreement {
  twrn: string;
  name: string;
  status: AgreementStatusEnum;
  amount: number;
  original_agreement_amount: number;
  agreement_type: AgreementTypeEnum;
  date_created: string;
  expiration_date: string;
  description: string | null;
  contact_entity_name: string | null;
  contact_point_of_contact: string | null;
  contact_email: string | null;
  academic_year_date_id: string | null;
}

export class Agreement {
  twrn: string;
  name: string;
  status: AgreementStatusEnum;
  agreementType: AgreementTypeEnum;
  athleteProfiles: AthleteProfile[];
  /* Amount in dollars and cents */
  amount: number;
  originalAgreementAmount: number;
  dateCreated: string;
  totalAthletes: number;
  budget: AgreementBudget | null;
  budgets: AgreementBudget[];
  canUpdateBudget: boolean;

  constructor(data: ApiAgreement) {
    this.twrn = data.twrn;
    this.name = data.name;
    this.status = data.status;
    this.agreementType = data.agreement_type;
    this.athleteProfiles = data.athlete_profiles.map((profile) => new AthleteProfile(profile));
    this.amount = convertCentsToDollarsDecimal(data.amount);
    this.originalAgreementAmount = convertCentsToDollarsDecimal(data.original_agreement_amount);
    this.dateCreated = data.date_created;
    this.totalAthletes = data.total_athletes;
    this.budget = data.budget;
    this.budgets = data.budgets ?? (data.budget ? [data.budget] : []);
    this.canUpdateBudget = data.can_update_budget;
  }

  static fromAthleteAgreement(data: ApiAthleteAgreement): Agreement {
    return new Agreement({
      twrn: data.twrn,
      name: data.name,
      status: data.status,
      agreement_type: data.agreement_type,
      athlete_profiles: [],
      total_athletes: 0,
      amount: data.amount,
      original_agreement_amount: data.original_agreement_amount,
      date_created: data.date_created,
      budget: null,
      can_update_budget: false,
    });
  }
}

export interface ApiTeamAgreementAthlete {
  athlete_name: string;
  athlete_twrn: string;
  agreement_athlete_status: AgreementAthleteStatusEnum;
  agreement_athlete_twrn: string;
  agreement_name: string;
  agreement_twrn: string;
  last_updated_date: string;
  total_contracted: number;
}

export class TeamAgreementAthlete {
  athlete: {
    name: string;
    twrn: string;
  };

  agreement: {
    athleteStatus: AgreementAthleteStatusEnum;
    twrn: string;
    athleteTwrn: string;
    name: string;
  };

  lastUpdatedDate: string;
  totalContracted: number;

  constructor(data: ApiTeamAgreementAthlete) {
    this.athlete = {
      name: data.athlete_name,
      twrn: data.athlete_twrn,
    };
    this.agreement = {
      athleteStatus: data.agreement_athlete_status,
      twrn: data.agreement_twrn,
      athleteTwrn: data.agreement_athlete_twrn,
      name: data.agreement_name,
    };
    this.lastUpdatedDate = data.last_updated_date;
    this.totalContracted = convertCentsToDollarsDecimal(data.total_contracted);
  }
}

export interface ApiDraftAgreement {
  team_budget: string | null;
  team_budgets: string[] | null;
  contract_template: string;
  name: string;
  academic_year_date_id: string | null;
  academic_year_date_ids: string[] | null;
  status: AgreementStatusEnum;
  agreement_type: AgreementTypeEnum;
  payment_type: PaymentTypeEnum;
  expiration_date: string | null;
  description: string | null;
  attach_payment_schedule: PaymentScheduleAttachmentLocationEnum | null;
  contact_entity_name: string;
  contact_point_of_contact: string;
  contact_email: string;
  frequency_type: FrequencyTypeEnum;
  weekly_day: DayOfWeekEnum;
  twice_a_month_option: TwiceAMonthOptionEnum;
  payment_start_date: string | null;
  payment_end_date: string | null;
  custom_payment_schedule: string[] | null;
  athletes: Array<AthletePaymentsRequest>;
  participants: ApiAgreementParticipant[];
}

export class DraftAgreement {
  teamBudget: string | null;
  teamBudgets: string[] | null;
  contractTemplate: string;
  name: string;
  academicYearDateId: string | null;
  academicYearDateIds: string[] | null;
  status: AgreementStatusEnum;
  agreementType: AgreementTypeEnum;
  paymentType: PaymentTypeEnum;
  expirationDate: string | null;
  description: string | null;
  attachPaymentSchedule: PaymentScheduleAttachmentLocationEnum | null;
  contactEntityName: string;
  contactPointOfContact: string;
  contactEmail: string;
  frequencyType: FrequencyTypeEnum;
  weeklyDay: DayOfWeekEnum;
  twiceAMonthOption: TwiceAMonthOptionEnum;
  paymentStartDate: string | null;
  paymentEndDate: string | null;
  customPaymentSchedule: string[] | null;
  athletes: Array<AthletePaymentsRequest>;
  signees: AgreementSignee[];

  constructor(data: ApiDraftAgreement) {
    this.teamBudget = data.team_budget;
    this.teamBudgets = data.team_budgets ?? null;
    this.contractTemplate = data.contract_template;
    this.name = data.name;
    this.academicYearDateId = data.academic_year_date_id;
    this.academicYearDateIds = data.academic_year_date_ids ?? [];
    this.status = data.status;
    this.agreementType = data.agreement_type;
    this.paymentType = data.payment_type;
    this.expirationDate = data.expiration_date;
    this.description = data.description;
    this.attachPaymentSchedule = data.attach_payment_schedule;
    this.contactEntityName = data.contact_entity_name;
    this.contactPointOfContact = data.contact_point_of_contact;
    this.contactEmail = data.contact_email;
    this.frequencyType = data.frequency_type;
    this.weeklyDay = data.weekly_day;
    this.twiceAMonthOption = data.twice_a_month_option;
    this.paymentStartDate = data.payment_start_date;
    this.paymentEndDate = data.payment_end_date;
    this.customPaymentSchedule = data.custom_payment_schedule;
    this.athletes = data.athletes;
    this.signees = AgreementForm.makeSignees(data.participants);
  }
}

export interface ApiContractTemplate {
  twrn: string;
  name: string;
  signing_groups: ApiSigningGroup[] | null;
}
export interface ApiContractTemplateFull extends ApiContractTemplate {
  link: ApiContractTemplateLink;
}
export interface ApiContractTemplateLink {
  url: string;
  expires_at: string;
}

export interface ApiContractTemplateFull extends ApiContractTemplate {
  link: ApiContractTemplateLink;
}


export class ContractTemplate {
  twrn: string;
  name: string;
  link?: string;
  signingGroups: {
    twrn: string;
    name: string;
  }[];

  constructor(data: ApiContractTemplate | ApiContractTemplateFull) {
    this.twrn = data.twrn;
    this.name = data.name;
    if (typeof data === 'object' && 'link' in data && data.link) {
      this.link = data.link.url;
    } else if (typeof data === 'string') {
      this.link = data;
    }
    this.signingGroups = data.signing_groups ?? [];
  }
}

export interface ContractTemplateResponse {
  templates: ContractTemplate[];
  metadata: ApiPaginationMetadata;
}

export interface ApiAgreementBudget {
  twrn: string;
  name: string;
}

export class AgreementBudget {
  twrn: string;
  name: string;

  constructor(data: ApiAgreementBudget) {
    this.twrn = data.twrn;
    this.name = data.name;
  }
}

export type ApiAgreementDetails = {
  twrn: string;
  name: string;
  academic_year_date_id: string | null;
  academic_year_date_ids?: string[];
  status: AgreementStatusEnum;
  original_agreement_amount: number;
  total_athletes: number;
  agreement_type: AgreementTypeEnum;
  contract_template: ApiContractTemplateFull | null;
  date_created: string | null;
  expiration_date: string;
  description: string | null;
  contact_entity_name: string | null;
  contact_point_of_contact: string | null;
  contact_email: string | null;
  budget: ApiAgreementBudget | null;
  budgets?: ApiAgreementBudget[];
  can_update_budget: boolean;
};

export class AgreementDetails {
  twrn: string;
  name: string;
  academicYearDateId: string | null;
  academicYearDateIds: string[];
  status: AgreementStatusEnum;
  originalAgreementAmount: number;
  totalAthletes: number;
  agreementType: AgreementTypeEnum;
  contractTemplate: ContractTemplate | null;
  dateCreated: string | null;
  expirationDate: string;
  description: string | null;
  contactEntityName: string | null;
  contactPointOfContact: string | null;
  contactEmail: string | null;
  budget: AgreementBudget | null;
  budgets: AgreementBudget[];
  canUpdateBudget: boolean;

  constructor(data: ApiAgreementDetails) {
    this.twrn = data.twrn;
    this.name = data.name;
    this.academicYearDateId = data.academic_year_date_id ?? null;
    this.academicYearDateIds = data.academic_year_date_ids ?? (data.academic_year_date_id ? [data.academic_year_date_id] : []);
    this.status = data.status;
    this.originalAgreementAmount = convertCentsToDollarsDecimal(data.original_agreement_amount);
    this.totalAthletes = data.total_athletes;
    this.agreementType = data.agreement_type;
    this.contractTemplate = data.contract_template ? new ContractTemplate(data.contract_template) : null;
    this.dateCreated = data.date_created;
    this.expirationDate = data.expiration_date;
    this.description = data.description;
    this.contactEntityName = data.contact_entity_name;
    this.contactPointOfContact = data.contact_point_of_contact;
    this.contactEmail = data.contact_email;
    this.budget = data.budget ? new AgreementBudget(data.budget) : null;
    this.budgets = (data.budgets?.length ? data.budgets : (data.budget ? [data.budget] : [])).map((b) => new AgreementBudget(b));
    this.canUpdateBudget = data.can_update_budget;
  }
}

export interface ApiAthleteProfile {
  twrn: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string | null;
  jersey_number: string;
}

export interface ApiAgreementStats {
  total_athletes: number;
  total_agreements_signed: number;
}

export class AgreementStats {
  totalAthletes: number;
  totalAgreementsSigned: number;

  constructor(data: ApiAgreementStats) {
    this.totalAthletes = data.total_athletes;
    this.totalAgreementsSigned = data.total_agreements_signed;
  }
}

export const CONTRACT_REVIEWER_STATUS_ENUM = {
  PENDING: 0,
  ELIGIBLE: 1,
  ANNOTATED: 2,
  REJECTED: 3,
  ACCEPTED: 4,
} as const;

export type ContractReviewerStatusEnum = typeof CONTRACT_REVIEWER_STATUS_ENUM[keyof typeof CONTRACT_REVIEWER_STATUS_ENUM];

export interface ApiAgreementAthleteReviewer {
  first_name: string;
  last_name: string;
  email: string;
  status: ContractReviewerStatusEnum;
}

export class AgreementAthleteReviewer {
  firstName: string;
  lastName: string;
  email: string;
  status: ContractReviewerStatusEnum;

  constructor(data: ApiAgreementAthleteReviewer) {
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.email = data.email;
    this.status = data.status;
  }
}

export type ApiAgreementAthlete = {
  twrn: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string | null;
  jersey_number: string | null;
  position_id: string | null;
  academic_year: AcademicYearEnum | null;
  agreement_athlete_status: AgreementAthleteStatusEnum;
  total_contracted: number;
  last_updated_date: string;
  agreement_athlete_twrn: string;
  tags: string[];
  has_tw_profile: boolean;
  can_delete: boolean;
  reviewers?: ApiAgreementAthleteReviewer[];
};
export class AgreementAthlete {
  twrn: string;
  firstName: string;
  lastName: string;
  fullName: string;
  profilePhotoUrl?: string | null;
  avatar?: string | null;
  jerseyNumber: string | null;
  position: string | null;
  academicYear: AcademicYearEnum | null;
  agreementAthleteStatus: AgreementAthleteStatusEnum;
  totalContracted: number;
  lastUpdatedDate: string;
  agreementAthleteTwrn: string;
  tags: string[];
  hasTwProfile: boolean;
  canDelete: boolean;
  reviewers: AgreementAthleteReviewer[];

  constructor(data: ApiAgreementAthlete, teamTwrn?: string) {
    this.twrn = data.twrn;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.fullName = `${data.first_name} ${data.last_name}`;
    this.profilePhotoUrl = data.profile_photo_url;
    this.jerseyNumber = data.jersey_number;

    // Resolve position from UUID, fall back to legacy position string, then null
    const resolvedPosition = teamTwrn ? TeamPosition.resolveName(teamTwrn, data.position_id) : null;
    this.position = resolvedPosition ?? null;

    this.academicYear = data.academic_year;
    this.agreementAthleteStatus = data.agreement_athlete_status;
    this.totalContracted = convertCentsToDollarsDecimal(data.total_contracted);
    this.lastUpdatedDate = data.last_updated_date;
    this.agreementAthleteTwrn = data.agreement_athlete_twrn;
    this.tags = data.tags;
    this.hasTwProfile = data.has_tw_profile;
    this.canDelete = data.can_delete;
    this.reviewers = (data.reviewers ?? []).map((reviewer) => new AgreementAthleteReviewer(reviewer));
  }

  /**
   * Returns props for AthleteDisplayItem component
   */
  toDisplayProps(options?: {
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
    hideChips?: boolean;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.profilePhotoUrl,
        useInitials: true,
      },
      jerseyNumber: this.jerseyNumber,
      position: this.position,
      academicYear: this.academicYear,
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
      hideChips: options?.hideChips,
    };
  }
}

// Contract Documents

export type ApiExpiringDocumentLink = {
  url: string;
  expires_at: string;
};


export type ApiContractDocument = {
  name: string;
  key: string;
  collection: CollectionEnum;
  link: ApiExpiringDocumentLink;
};

export type ApiContractRecipient = {
  id: string;
  twrn: string;
  url: string;
};

export class ContractRecipient {
  id: string;
  twrn: string;
  url: string;

  constructor(data: ApiContractRecipient) {
    this.id = data.id;
    this.twrn = data.twrn;
    this.url = data.url;
  }
}

export type ApiContractResponse = {
  twrn: string;
  recipients: ApiContractRecipient[];
  documents: ApiContractDocument[];
};

export class ContractResponse {
  twrn: string;
  recipients: ContractRecipient[];
  documents: ContractDocument[];

  constructor(data: ApiContractResponse) {
    this.twrn = data.twrn;
    this.documents = data.documents ? data.documents.map((document) => new ContractDocument(document)) : [];
    this.recipients = data.recipients ? data.recipients.map((recipient) => new ContractRecipient(recipient)) : [];
  }
}



export type ApiContractParticipant = {
  name: string;
  email: string;
  is_athlete: boolean;
  signing_order: number;
  signing_group_twrn: string;
  contract_recipient_twrn: string;
  latest_status: SignatureStatusEnum | null;
  latest_updated_at: string | null;
};

export class ContractParticipant {
  name: string;
  email: string;
  isAthlete: boolean;
  signingOrder: number;
  signingGroupTwrn: string;
  contractRecipientTwrn: string;
  latestStatus: SignatureStatusEnum | null;
  latestUpdatedAt: string | null;

  constructor(data: ApiContractParticipant) {
    this.name = data.name;
    this.email = data.email;
    this.isAthlete = data.is_athlete;
    this.signingOrder = data.signing_order;
    this.signingGroupTwrn = data.signing_group_twrn;
    this.contractRecipientTwrn = data.contract_recipient_twrn;
    this.latestStatus = data.latest_status;
    this.latestUpdatedAt = data.latest_updated_at;
  }
}

export type ApiAthleteContract = {
  twrn: string;
  athlete_twrn: string;
  latest_status: AgreementAthleteStatusEnum | null;
  latest_updated_at: string | null;
  total_contracted: number;
  contract: ApiContractResponse;
  participants: ApiContractParticipant[] | null;
  reviewers: ApiAgreementAthleteReviewer[] | null;
};

export class ExpiringDocumentLink {
  url: string;
  expiresAt: string;

  constructor(data: ApiExpiringDocumentLink) {
    this.url = data.url;
    this.expiresAt = data.expires_at;
  }
}

export class ContractDocument {
  name: string;
  key: string;
  collection: CollectionEnum;
  link: ExpiringDocumentLink;

  constructor(data: ApiContractDocument) {
    this.name = data.name;
    this.key = data.key;
    this.collection = data.collection;
    this.link = new ExpiringDocumentLink(data.link);
  }
}

export class AthleteContract {
  twrn: string;
  athleteTwrn: string;
  latestStatus: AgreementAthleteStatusEnum | null;
  latestUpdatedAt: string | null;
  totalContracted: number;
  contract: ContractResponse | null;
  participants: ContractParticipant[] | null;
  reviewers: AgreementAthleteReviewer[];
  constructor(data: ApiAthleteContract) {
    this.twrn = data.twrn;
    this.athleteTwrn = data.athlete_twrn;
    this.latestStatus = data.latest_status;
    this.latestUpdatedAt = data.latest_updated_at;
    this.totalContracted = convertCentsToDollarsDecimal(data.total_contracted);
    this.contract = data.contract ? new ContractResponse(data.contract) : null;
    this.participants = data.participants && data.participants.length > 0 ? data.participants.map((participant) => new ContractParticipant(participant)) : null;
    this.reviewers = (data.reviewers ?? []).map((reviewer) => new AgreementAthleteReviewer(reviewer));
  }
}

export type ScholarshipAwardCategoryPreview = {
  name: string;
  value: number;
};

export type ScholarshipAcademicYearTerm = {
  academic_year: string;
  scholarship_value: number | null;
  // The athlete's calculated scholarship value, always present and NOT gated on any
  // include-toggle (unlike scholarship_value, which the user can suppress). The contract
  // service ignores it; it exists so the create/update total can be summed even when
  // scholarship_value is omitted from the addendum.
  budget_value: number;
  package_name: string | null;
  award_categories: ScholarshipAwardCategoryPreview[];
  scholarship_equivalency: string | null;
  scholarship_equivalency_pct: string | null;
};

export interface GenerateAgreementPreviewRequestModel {
  contract_template: string;
  name: string;
  description?: string | null;
  attach_payment_schedule: PaymentScheduleAttachmentLocationEnum | null;
  contact_point_of_contact: string;
  athlete: string;
  agreement_type?: AgreementTypeEnum;
  payments?: {
    value: number;
    date: string;
  }[];
  academic_year_terms?: ScholarshipAcademicYearTerm[] | null;
}

export const isGenerateAgreementPreviewRequestModel = (data: any): data is GenerateAgreementPreviewRequestModel => {
  // attach_payment_schedule is nullable and payments is optional on the interface
  // (scholarship previews carry academic_year_terms instead), so the guard only
  // asserts the always-required fields. Callers that need non-empty payments
  // (the rev-share branch) check that explicitly before building the payload.
  return typeof data === 'object'
    && data !== null
    && 'contract_template' in data
    && data.contract_template !== null
    && 'name' in data
    && data.name !== null
    && 'attach_payment_schedule' in data
    && 'contact_point_of_contact' in data
    && data.contact_point_of_contact !== null
    && 'athlete' in data
    && data.athlete !== null;
};

export type ApiAgreementPreview = {
  mime_type: string;
  content: string;
};

export class AgreementPreview {
  mimeType: string;
  content: string;

  constructor(data: ApiAgreementPreview) {
    this.mimeType = data.mime_type;
    this.content = data.content;
  }
}

// Server-side pagination and sorting types for agreements
export type ApiAgreementListingSortParams = ApiSortParams<'date' | 'name'>;

export interface ApiAgreementListingRequestBody {
  per_page?: number;
  cursor?: string | null;
  sort?: ApiAgreementListingSortParams;
  keyword?: string;
  athletes?: string[];
  status?: AgreementStatusEnum[];
  // TODO(GM-3004-be): waiting on BE to accept `agreement_type` as a list-endpoint filter
  // (gm-api !1172 only added it to response payloads). Send-side wiring stays inert until then.
  agreement_type?: AgreementTypeEnum[];
  dates?: {
    start: string;
    end: string;
  };
}

export type AgreementSortField = ApiAgreementListingSortParams['field'];
export type AgreementSortDirection = ApiAgreementListingSortParams['direction'];


// Server-side pagination and sorting types for agreements by contract
export type ApiAgreementByContractSortParams = ApiSortParams<'full_name' | 'name' | 'status' | 'date'>;


export type AgreementByContractSortField = ApiAgreementByContractSortParams['field'];
export type AgreementByContractSortDirection = ApiAgreementByContractSortParams['direction'];

export interface ApiAgreementByContractRequestBody {
  per_page?: number;
  cursor?: string | null;
  sort?: ApiAgreementByContractSortParams;
  keyword?: string;
  athletes?: string[];
  status?: AgreementAthleteStatusEnum[];
  dates?: {
    start: string;
    end: string;
  };
}
